/**
 * Workflow Graph Operations — Single Source of Truth
 *
 * Pure functions that operate on immutable Omega-format graphs.
 * Used by BOTH the AI chat tool dispatcher AND the builder UI.
 * No React, no browser APIs — fully testable.
 *
 * Omega graph shape:
 *   nodes: WorkflowNode[] — discriminated union on `type`
 *   edges: WorkflowEdge[] — { id, from, to }
 *
 * The branching/insertion logic here is the canonical implementation.
 * The builder's useWorkflowNodeAddition delegates to these operations
 * via convert(RF→Omega) → op → convert(Omega→RF) → layout.
 */

import type { WorkflowEdge, WorkflowNode } from "@/types/workflows";
import { isContainerOmegaNode } from "@/types/workflows";
import type { ObjectSchema, Schema } from "@/types/tools";
import { toSnakeCase } from "@/utils/common/strings";
import { getId } from "../../../../lib/generate-id";
import {
  getDefaultParametersForType,
  getNodeLabel,
  isValidNodeType,
  nodeHasFailureRoute,
  nodeIsBranching,
  nodeIsContainer,
  nodeIsTerminal,
  nodeIsTrigger,
} from "./workflow-node-registry";
import {
  addSubGraph as addSubGraphOperation,
  addForEach as addForEachOperation,
  findContainingGraph,
  getSubGraphDescription,
  insertSubGraphBetween,
  insertForEachBetween,
  replaceInnerGraph,
} from "./subgraph-workflow-graph-operations";
import type {
  OmegaGraph,
  WorkflowBranch,
  WorkflowStep,
} from "./workflow-chat-types";

// =============================================================================
// Canonical start node id
// =============================================================================

/**
 * The fixed id of the auto-generated start node. The AI references tool inputs
 * as `node.start.payload.*`, so the start node MUST carry this exact id or
 * those CEL expressions fail Omega validation. Single source of truth shared by
 * graph construction, schema inference, and the chat system prompt.
 */
export const START_NODE_ID = "start";

// =============================================================================
// Tool exposure — input-schema inference
// =============================================================================

// Matches `start.payload.<field>` (JS identifier) anywhere in a serialized
// graph. Built from START_NODE_ID so the two can't drift. The leading
// negative lookbehind keeps it from matching a node id that merely ends in
// "start" (e.g. `restart.payload.x`).
const START_PAYLOAD_REF_REGEX = new RegExp(
  String.raw`(?<![A-Za-z0-9_$])${START_NODE_ID}\.payload\.([A-Za-z_$][A-Za-z0-9_$]*)`,
  "g"
);

/**
 * Infers the tool input schema from the `node.start.payload.<field>` refs the
 * graph uses, declaring each as a required string. Deterministic fallback when
 * the AI exposes a build as a tool without a hand-authored `inputSchema`.
 */
export function inferStartInputSchemaFromGraph(graph: OmegaGraph): ObjectSchema {
  const fields = new Set<string>();
  for (const match of JSON.stringify(graph).matchAll(START_PAYLOAD_REF_REGEX)) {
    if (match[1]) fields.add(match[1]);
  }
  // Sort so the schema is stable regardless of match order (avoids noisy diffs).
  const sorted = [...fields].sort((a, b) => a.localeCompare(b));

  const properties: Record<string, Schema> = {};
  for (const field of sorted) {
    properties[field] = {
      type: "string",
      description: `Value for ${field}.`,
      hasDefault: false,
    };
  }

  return {
    type: "object",
    description: "Inputs this workflow needs to run.",
    properties,
    required: sorted,
  };
}

/**
 * Forces snake_case on a workflow's tool-input keys so the exposed tool's
 * parameter names (the Start node `inputSchema`) and the in-graph
 * `node.start.payload.<field>` references can't disagree at runtime. Rewrites
 * both the references (first segment only — nested keys are the caller's
 * object shape) and the Start node's `inputSchema` keys/`required`. Idempotent.
 */
export function normalizeStartPayloadKeys(graph: OmegaGraph): OmegaGraph {
  // Operate on the serialized form so it reaches CEL, `{{ }}` templates, and
  // branch conditions in one pass.
  const rewritten = JSON.stringify(graph).replace(
    START_PAYLOAD_REF_REGEX,
    (_match, field: string) => `${START_NODE_ID}.payload.${toSnakeCase(field)}`
  );
  const next = JSON.parse(rewritten) as OmegaGraph;

  next.nodes = next.nodes.map((n) => {
    if (n.type !== "start") return n;
    const node = n as WorkflowNode & {
      inputSchema?: {
        properties?: Record<string, unknown>;
        required?: string[];
      };
    };
    const schema = node.inputSchema;
    if (!schema?.properties) return n;
    const properties: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(schema.properties)) {
      properties[toSnakeCase(key)] = value;
    }
    const required = Array.isArray(schema.required)
      ? schema.required.map(toSnakeCase)
      : schema.required;
    return {
      ...node,
      inputSchema: { ...schema, properties, required },
    } as WorkflowNode;
  });

  return next;
}

// =============================================================================
// RF → Omega type mapping
// =============================================================================

/**
 * Map from ReactFlow node types to Omega node types.
 * Only includes types where the RF key differs from the Omega key.
 */
const RF_TO_OMEGA_TYPE: Record<string, string> = {
  data_transform: "pipe",
  SlackMessage: "slack_post_channel_message", // default subtype
  ghost: "noop",
};

/**
 * Convert a ReactFlow node type to the corresponding Omega node type.
 * For most types the values are identical; only a few differ.
 */
export function rfToOmegaNodeType(rfType: string): string {
  return RF_TO_OMEGA_TYPE[rfType] ?? rfType;
}

// =============================================================================
// Default Omega params per node type
// =============================================================================

/**
 * Returns sensible Omega-format default parameters for a given node type.
 * Used by the builder so newly-created nodes start with a useful config.
 * The AI chat typically provides its own params from the user's instructions.
 *
 * NOTE: This provides UI-friendly overrides (e.g. branches array for split,
 * max=100 for random_number). The registry's `getDefaultParametersForType`
 * provides schema-level zero-value defaults. Both are merged inside
 * `makeNode → mergeWithDefaults`, where these overrides take precedence.
 */
export function getDefaultOmegaParams(
  nodeType: string
): Record<string, unknown> {
  switch (nodeType) {
    case "split":
      return {
        branches: [
          {
            label: "If",
            check: { type: "cel", value: "true" },
          },
        ],
      };
    case "delay":
      return { until: { type: "literal", value: "00:05:00" } };
    case "random_number":
      return {
        min: { type: "literal", value: 0 },
        max: { type: "literal", value: 100 },
      };
    case "google_drive_upload_file":
      // The schema-driven default would emit a literal FilePointer object
      // (`{ type: "literal", value: { storage_path: "", media_type: "" } }`)
      // because `omegaFilePointer` carries a Zod `.default(...)`. That lands
      // in node.data and renders as `[object Object]` in the (CEL-locked)
      // input. Force a CEL default here so newly-added nodes start with the
      // correct wire shape; users fill in their upstream reference.
      return {
        file: { type: "cel", value: "" },
      };
    case "google_drive_list_files":
      // Mirrors `getDefaultDataForNodeType` — must be one of
      // DriveListSort.AllNames; "name" is the most useful default.
      return {
        orderBy: { type: "literal", value: "name" },
      };
    case "google_sheets_append_rows":
      return {
        rows: { type: "cel", value: "" },
      };
    default:
      return {};
  }
}

// =============================================================================
// Helpers
// =============================================================================

/** Create an Omega-format edge */
function makeEdge(from: string, to: string): WorkflowEdge {
  return { id: `edge_${from}_${to}`, from, to };
}

/** Create a minimal Omega-format node */
function makeNode(
  type: string,
  name: string,
  params?: Record<string, unknown>,
  id?: string
): WorkflowNode {
  const base = {
    id: id ?? getId(type),
    name,
    type,
    displayInfo: { position: { x: 0, y: 0 } },
  };

  // Merge AI-provided params over schema-derived defaults so that any
  // fields the AI omits still get a proper { type: "literal", value: ... }.
  const nodeParams = mergeWithDefaults(type, params);

  if (nodeParams !== undefined) {
    return { ...base, parameters: nodeParams } as WorkflowNode;
  }
  return base as WorkflowNode;
}

/** Create a noop node (ghost/placeholder) */
function makeNoop(name = "Noop"): WorkflowNode {
  return makeNode("noop", name);
}

/**
 * Merge AI-provided params over schema-derived defaults for a node type.
 * Ensures every field the transformer expects has at least a default value.
 *
 * - If the type has no schema (end, noop): returns `params` as-is (may be undefined).
 * - If the AI passes params: schema defaults are filled in for any missing fields.
 * - If the AI passes nothing: full schema defaults are used.
 */
function mergeWithDefaults(
  type: string,
  params?: Record<string, unknown>
): Record<string, unknown> | undefined {
  const defaults = getDefaultParametersForType(type);
  if (!defaults) return params;
  if (!params) return defaults;
  return { ...defaults, ...params };
}

/**
 * Ensure a node from an external source (e.g., AI's set_graph) has proper
 * `parameters` with all required fields filled in from schema defaults.
 *
 * The AI sometimes places parameter values in a `params` field (matching
 * the tool-call argument name) instead of the Omega-standard `parameters`.
 * We check both and prefer `parameters`, falling back to `params`.
 */
function normalizeNode(node: WorkflowNode): WorkflowNode {
  const existing =
    getNodeParams(node) ??
    (node as unknown as Record<string, unknown>).params as
      | Record<string, unknown>
      | undefined;
  const merged = mergeWithDefaults(node.type, existing);
  if (merged === undefined) return node;

  // Build the normalized node with `parameters`; strip stale `params` key.
  const { params: _discarded, ...rest } = node as unknown as Record<
    string,
    unknown
  >;
  return { ...rest, parameters: merged } as unknown as WorkflowNode;
}

/** Find all edges leaving a node */
function outgoingEdges(graph: OmegaGraph, nodeId: string): WorkflowEdge[] {
  return graph.edges.filter((e) => e.from === nodeId);
}

/** Find all edges entering a node */
function incomingEdges(graph: OmegaGraph, nodeId: string): WorkflowEdge[] {
  return graph.edges.filter((e) => e.to === nodeId);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Type-safe access to a node's parameters */
function getNodeParams(
  node: WorkflowNode
): Record<string, unknown> | undefined {
  return (node as WorkflowNode & { parameters?: Record<string, unknown> })
    .parameters;
}

// =============================================================================
// Special Node Setup — branching & failure routes
// =============================================================================

interface BranchSpec {
  label?: string;
  check?: unknown;
  [key: string]: unknown;
}

/**
 * Set up failure-route node wiring (success path only by default).
 * - Creates a Success noop (no Failure noop, no `onFailureEdgeId`) until the user
 *   opts in via "On failure" in the builder.
 * - If `targetId` is provided (insertion), removes the direct newNode→target
 *   edge and routes: newNode → successNoop → target.
 */
function setupFailureRoute(
  newNode: WorkflowNode,
  nodes: WorkflowNode[],
  edges: WorkflowEdge[],
  targetId?: string
): { nodes: WorkflowNode[]; edges: WorkflowEdge[] } {
  let filteredEdges = edges;

  // For insertion: remove the direct newNode → target edge
  if (targetId) {
    filteredEdges = edges.filter(
      (e) => !(e.from === newNode.id && e.to === targetId)
    );
  }

  const successNoop = makeNoop("Success");
  const successEdge = makeEdge(newNode.id, successNoop.id);

  const newEdges = [...filteredEdges, successEdge];

  // For insertion: connect success noop → target to preserve flow
  if (targetId) {
    newEdges.push(makeEdge(successNoop.id, targetId));
  }

  const updatedNodes = [...nodes, successNoop];

  return { nodes: updatedNodes, edges: newEdges };
}

/**
 * Set up branching (conditional) for a node.
 * - Creates a noop for each pre-existing branch in params.branches.
 * - Always creates a Default noop.
 * - If `targetId` is provided (insertion), removes the direct newNode→target
 *   edge and routes: newNode → defaultNoop → target.
 * - Sets `defaultEdgeId` and `edgeId` on each branch.
 */
function setupBranching(
  newNode: WorkflowNode,
  nodes: WorkflowNode[],
  edges: WorkflowEdge[],
  targetId?: string
): { nodes: WorkflowNode[]; edges: WorkflowEdge[] } {
  let filteredEdges = edges;

  // For insertion: remove the direct newNode → target edge
  if (targetId) {
    filteredEdges = edges.filter(
      (e) => !(e.from === newNode.id && e.to === targetId)
    );
  }

  const params = getNodeParams(newNode) ?? {};
  const initialBranches: BranchSpec[] = (params.branches as BranchSpec[]) ?? [];

  const additionalNodes: WorkflowNode[] = [];
  const additionalEdges: WorkflowEdge[] = [];
  const updatedBranches: BranchSpec[] = [];

  // Create a noop for each initial branch (e.g., the "If" branch)
  for (const branch of initialBranches) {
    const branchNoop = makeNoop(branch.label ?? "Branch");
    const branchEdge = makeEdge(newNode.id, branchNoop.id);
    additionalNodes.push(branchNoop);
    additionalEdges.push(branchEdge);
    updatedBranches.push({ ...branch, edgeId: branchEdge.id });
  }

  // Default noop — always present
  const defaultNoop = makeNoop("Default");
  const defaultEdge = makeEdge(newNode.id, defaultNoop.id);
  additionalNodes.push(defaultNoop);
  additionalEdges.push(defaultEdge);

  // For insertion: connect default noop → target to preserve flow
  if (targetId) {
    additionalEdges.push(makeEdge(defaultNoop.id, targetId));
  }

  // Update the new node's parameters with edgeIds
  const updatedNodes = [
    ...nodes.map((n) =>
      n.id === newNode.id
        ? {
            ...n,
            parameters: {
              ...params,
              branches: updatedBranches,
              defaultEdgeId: defaultEdge.id,
            },
          }
        : n
    ) as WorkflowNode[],
    ...additionalNodes,
  ];

  return { nodes: updatedNodes, edges: [...filteredEdges, ...additionalEdges] };
}

/**
 * Apply special node setup (failure routes, branching).
 *
 * @param targetId — when provided, the node is being inserted between
 *   source and target. The direct newNode→target edge will be removed and
 *   the flow will be routed through the appropriate noop nodes.
 *   When omitted, noop nodes dangle (append scenario).
 */
function applySpecialNodeSetup(
  newNode: WorkflowNode,
  nodeType: string,
  nodes: WorkflowNode[],
  edges: WorkflowEdge[],
  targetId?: string
): { nodes: WorkflowNode[]; edges: WorkflowEdge[] } {
  let result = { nodes, edges };

  if (nodeHasFailureRoute(nodeType)) {
    result = setupFailureRoute(newNode, result.nodes, result.edges, targetId);
  }

  if (nodeIsBranching(nodeType)) {
    result = setupBranching(newNode, result.nodes, result.edges, targetId);
  }

  return result;
}

// =============================================================================
// addNodeAfter
// =============================================================================

/**
 * Add a new node after an existing node.
 *
 * - If afterNode has outgoing edge(s): splits the first edge.
 *   (old: afterNode → X, new: afterNode → newNode → X)
 *   For branching/failure nodes, flow is routed through noop nodes.
 * - If afterNode has 0 outgoing edges: just connect afterNode → newNode.
 * - Creates a Success noop for failure-route nodes (failure branch is opt-in).
 * - Creates branch + default noop nodes for branching nodes.
 *
 * @param name — optional display name; defaults to the registry label.
 */
export function addNodeAfter(
  graph: OmegaGraph,
  afterNodeId: string,
  nodeType: string,
  params?: Record<string, unknown>,
  name?: string,
  id?: string
): OmegaGraph {
  if (!isValidNodeType(nodeType)) {
    throw new Error(`Invalid node type: ${nodeType}`);
  }

  const afterNode = graph.nodes.find((n) => n.id === afterNodeId);
  if (!afterNode) {
    throw new Error(`Node not found: ${afterNodeId}`);
  }

  const label = name ?? getNodeLabel(nodeType);
  if (nodeIsContainer(nodeType)) {
    const containerAdd = nodeType === "foreach" ? addForEachOperation : addSubGraphOperation;
    return containerAdd(
      graph,
      afterNodeId,
      label,
      getSubGraphDescription(params),
      id
    );
  }

  const newNode = makeNode(nodeType, label, params, id);
  const outEdges = outgoingEdges(graph, afterNodeId);

  let nodes = [...graph.nodes, newNode];
  let edges = [...graph.edges];
  let targetId: string | undefined;

  if (outEdges.length >= 1) {
    // Split the first outgoing edge: afterNode → newNode → target
    const oldEdge = outEdges[0];
    targetId = oldEdge.to;
    edges = [
      ...edges.filter((e) => e.id !== oldEdge.id),
      makeEdge(afterNodeId, newNode.id),
      makeEdge(newNode.id, targetId),
    ];
  } else {
    // No outgoing edge: just connect
    edges.push(makeEdge(afterNodeId, newNode.id));
  }

  // Handle failure route nodes and branching — pass targetId for insertion routing
  ({ nodes, edges } = applySpecialNodeSetup(
    newNode,
    nodeType,
    nodes,
    edges,
    targetId
  ));

  return { nodes, edges };
}

// =============================================================================
// insertNodeBetween
// =============================================================================

/**
 * Insert a node between two connected nodes by splitting the edge.
 * source → target  becomes  source → newNode → target
 *
 * For branching/failure nodes, the direct newNode → target edge is replaced
 * by routing through the appropriate noop nodes.
 *
 * @param name — optional display name; defaults to the registry label.
 */
export function insertNodeBetween(
  graph: OmegaGraph,
  sourceId: string,
  targetId: string,
  nodeType: string,
  params?: Record<string, unknown>,
  name?: string,
  id?: string
): OmegaGraph {
  if (!isValidNodeType(nodeType)) {
    throw new Error(`Invalid node type: ${nodeType}`);
  }

  const edge = graph.edges.find(
    (e) => e.from === sourceId && e.to === targetId
  );
  if (!edge) {
    throw new Error(`No edge found from ${sourceId} to ${targetId}`);
  }

  const label = name ?? getNodeLabel(nodeType);
  if (nodeIsContainer(nodeType)) {
    const containerInsert = nodeType === "foreach" ? insertForEachBetween : insertSubGraphBetween;
    return containerInsert(
      graph,
      sourceId,
      targetId,
      label,
      getSubGraphDescription(params),
      id
    );
  }

  const newNode = makeNode(nodeType, label, params, id);

  let nodes = [...graph.nodes, newNode];
  let edges = [
    ...graph.edges.filter((e) => e.id !== edge.id),
    makeEdge(sourceId, newNode.id),
    makeEdge(newNode.id, targetId),
  ];

  // Handle failure route nodes and branching — pass targetId for insertion routing
  ({ nodes, edges } = applySpecialNodeSetup(
    newNode,
    nodeType,
    nodes,
    edges,
    targetId
  ));

  return { nodes, edges };
}

// =============================================================================
// deleteNode
// =============================================================================

/**
 * Collect noop node IDs that are direct children of a branching/failure node.
 */
function collectNoopChildren(
  graph: OmegaGraph,
  nodeId: string
): Set<string> {
  const ids = new Set<string>();
  for (const edge of outgoingEdges(graph, nodeId)) {
    const target = graph.nodes.find((n) => n.id === edge.to);
    if (target && target.type === "noop") {
      ids.add(target.id);
    }
  }
  return ids;
}

/**
 * Find the main successor of a node being deleted.
 * Checks direct non-noop successors first, then looks through noop children
 * for downstream non-noop targets (e.g., successNoop → target).
 */
function findMainSuccessor(
  graph: OmegaGraph,
  nodeId: string,
  noopIds: Set<string>
): string | undefined {
  const outEdges = outgoingEdges(graph, nodeId);

  // Direct non-noop successor
  const directEdge = outEdges.find((e) => {
    const t = graph.nodes.find((n) => n.id === e.to);
    return t && t.type !== "noop";
  });
  if (directEdge) return directEdge.to;

  // Check downstream of noop children
  for (const nId of noopIds) {
    const noopOutEdges = graph.edges.filter((e) => e.from === nId);
    const downstream = noopOutEdges.find((e) => {
      const t = graph.nodes.find((n) => n.id === e.to);
      return t && t.type !== "noop";
    });
    if (downstream) return downstream.to;
  }

  return undefined;
}

/**
 * Remove a node and reconnect its predecessors to its successors.
 *
 * Cannot delete start or end nodes.
 * If the node is branching: also removes child noop nodes.
 * If the node has a failure route: also removes the failure noop.
 */
export function deleteNode(graph: OmegaGraph, nodeId: string): OmegaGraph {
  const node = graph.nodes.find((n) => n.id === nodeId);
  if (!node) {
    throw new Error(`Node not found: ${nodeId}`);
  }
  if (nodeIsTrigger(node.type)) {
    throw new Error("Cannot delete the start node");
  }
  if (nodeIsTerminal(node.type)) {
    throw new Error("Cannot delete end nodes");
  }

  const inEdges = incomingEdges(graph, nodeId);
  const nodeIdsToRemove = new Set<string>([nodeId]);

  // Collect associated noop children for branching/failure nodes
  if (nodeIsBranching(node.type) || nodeHasFailureRoute(node.type)) {
    for (const id of collectNoopChildren(graph, nodeId)) {
      nodeIdsToRemove.add(id);
    }
  }

  const mainSuccessorId = findMainSuccessor(graph, nodeId, nodeIdsToRemove);

  // Remove all edges involving nodes being removed
  let edges = graph.edges.filter(
    (e) => !nodeIdsToRemove.has(e.from) && !nodeIdsToRemove.has(e.to)
  );

  // Reconnect predecessors to the main successor
  if (mainSuccessorId) {
    for (const inEdge of inEdges) {
      const alreadyExists = edges.some(
        (e) => e.from === inEdge.from && e.to === mainSuccessorId
      );
      if (!alreadyExists) {
        edges.push(makeEdge(inEdge.from, mainSuccessorId));
      }
    }
  }

  // Remove nodes
  const nodes = graph.nodes.filter((n) => !nodeIdsToRemove.has(n.id));

  return { nodes, edges };
}

// =============================================================================
// updateNode
// =============================================================================

/**
 * Update a node's parameters (shallow merge at the parameters level).
 * Each provided key in `params` replaces the corresponding entry in
 * `node.parameters`; keys not provided are preserved.
 * For nodes without parameters (start, end, noop), merges at the top level.
 */
export function updateNode(
  graph: OmegaGraph,
  nodeId: string,
  params: Record<string, unknown>
): OmegaGraph {
  const node = graph.nodes.find((n) => n.id === nodeId);
  if (!node) {
    throw new Error(`Node not found: ${nodeId}`);
  }

  const nodes = graph.nodes.map((n) => {
    if (n.id !== nodeId) return n;

    const existing = getNodeParams(n);
    if (existing) {
      return { ...n, parameters: { ...existing, ...params } } as WorkflowNode;
    }

    // For nodes without parameters, merge at top level — but `metadata` merges
    // one level deeper. No node's AI schema describes every key in that bag, so
    // replacing it from a partial payload drops fields: on Start, `toolId`
    // (which registers a second tool on the next publish) and
    // `gailWebhookEvents`. A `metadata` that is present but not an object still
    // replaces, which is the only way to clear the bag.
    const existingMetadata = (n as { metadata?: Record<string, unknown> }).metadata;
    const incomingMetadata = params.metadata;
    const merged = { ...n, ...params } as WorkflowNode;
    if (isRecord(existingMetadata) && isRecord(incomingMetadata)) {
      (merged as { metadata?: Record<string, unknown> }).metadata = {
        ...existingMetadata,
        ...incomingMetadata,
      };
    }
    return merged;
  });

  return { nodes, edges: graph.edges };
}

// =============================================================================
// setGraph
// =============================================================================

// =============================================================================
// setGraph — post-processing helpers
// =============================================================================

/** Result of processing a single branch — includes the (possibly updated) spec and any mutations. */
interface BranchNoopResult {
  spec: BranchSpec;
  noop?: WorkflowNode;
  edgeToNoop?: WorkflowEdge;
  edgeFromNoop?: WorkflowEdge;
  oldEdgeId?: string;
}

/** If a branch edge targets a non-noop node, return a noop to insert. */
function ensureBranchNoop(
  branch: BranchSpec,
  splitNodeId: string,
  nodes: WorkflowNode[],
  edges: WorkflowEdge[]
): BranchNoopResult {
  const branchEdge = edges.find((e) => e.id === branch.edgeId);
  if (!branchEdge) return { spec: branch };

  const target = nodes.find((n) => n.id === branchEdge.to);
  if (target?.type === "noop") return { spec: branch };

  const noop = makeNoop(branch.label ?? "Branch");
  const edgeToNoop = makeEdge(splitNodeId, noop.id);
  const edgeFromNoop = makeEdge(noop.id, branchEdge.to);

  return {
    spec: { ...branch, edgeId: edgeToNoop.id },
    noop,
    edgeToNoop,
    edgeFromNoop,
    oldEdgeId: branchEdge.id,
  };
}

/** If the default edge targets a non-noop node, return a noop to insert. */
function ensureDefaultNoop(
  params: Record<string, unknown>,
  splitNodeId: string,
  nodes: WorkflowNode[],
  edges: WorkflowEdge[]
): {
  defaultEdgeId: string | undefined;
  mutations?: {
    noop: WorkflowNode;
    edgeToNoop: WorkflowEdge;
    edgeFromNoop: WorkflowEdge;
    oldEdgeId: string;
  };
} {
  const defaultEdgeId = params.defaultEdgeId as string | undefined;
  if (!defaultEdgeId) return { defaultEdgeId };

  const defaultEdge = edges.find((e) => e.id === defaultEdgeId);
  if (!defaultEdge) return { defaultEdgeId };

  const target = nodes.find((n) => n.id === defaultEdge.to);
  if (target?.type === "noop") return { defaultEdgeId };

  const noop = makeNoop("Default");
  const edgeToNoop = makeEdge(splitNodeId, noop.id);
  const edgeFromNoop = makeEdge(noop.id, defaultEdge.to);

  return {
    defaultEdgeId: edgeToNoop.id,
    mutations: { noop, edgeToNoop, edgeFromNoop, oldEdgeId: defaultEdge.id },
  };
}

/**
 * Check whether a branching (split) node already has proper noop nodes for
 * each branch and the default edge. If a branch edge targets a non-noop node,
 * insert a noop in between so the builder renders ghost nodes correctly.
 *
 * Expected Omega structure for a split node:
 *   split → noop("If") → downstream
 *   split → noop("Default") → downstream
 */
function ensureBranchingNoops(graph: OmegaGraph): OmegaGraph {
  let nodes = [...graph.nodes];
  let edges = [...graph.edges];

  const branchingNodes = nodes.filter((n) => nodeIsBranching(n.type));

  for (const node of branchingNodes) {
    const params = getNodeParams(node);
    if (!params) continue;

    const branches = (params.branches as BranchSpec[]) ?? [];
    const updatedBranches = branches.map((branch) =>
      ensureBranchNoop(branch, node.id, nodes, edges)
    );

    // Collect mutations from branch processing
    for (const { noop, edgeToNoop, edgeFromNoop, oldEdgeId } of updatedBranches) {
      if (!noop) continue;
      edges = edges.filter((e) => e.id !== oldEdgeId);
      edges.push(edgeToNoop!, edgeFromNoop!);
      nodes.push(noop);
    }

    // Process default edge
    const { defaultEdgeId: newDefaultEdgeId, mutations: defaultMut } =
      ensureDefaultNoop(params, node.id, nodes, edges);
    if (defaultMut) {
      edges = edges.filter((e) => e.id !== defaultMut.oldEdgeId);
      edges.push(defaultMut.edgeToNoop, defaultMut.edgeFromNoop);
      nodes.push(defaultMut.noop);
    }

    // Update the split node's parameters with corrected edgeIds
    nodes = nodes.map((n) =>
      n.id === node.id
        ? ({
            ...n,
            parameters: {
              ...params,
              branches: updatedBranches.map((b) => b.spec),
              defaultEdgeId: newDefaultEdgeId,
            },
          } as unknown as WorkflowNode)
        : n
    );
  }

  return { nodes, edges };
}

/**
 * Insert a Success noop between one failure-route node and its downstream
 * targets, unless it already has a noop child. Nodes saved before their type
 * gained `hasFailureRoute` have a direct edge to their successor, so this also
 * runs when the failure branch is switched on (see `enableFailureRoute`).
 *
 * Expected Omega structure when failure handling is off:
 *   node → noop("Success") → downstream
 * When enabled, additionally:
 *   node → noop("Failure") (and node.onFailureEdgeId set to that edge)
 */
function ensureSuccessNoop(graph: OmegaGraph, nodeId: string): OmegaGraph {
  const outEdges = graph.edges.filter((e) => e.from === nodeId);
  const hasNoopChildren = outEdges.some(
    (e) => graph.nodes.find((n) => n.id === e.to)?.type === "noop"
  );

  if (hasNoopChildren) return graph;

  const successNoop = makeNoop("Success");
  const edges = graph.edges.filter((e) => e.from !== nodeId);
  edges.push(makeEdge(nodeId, successNoop.id));

  for (const out of outEdges) {
    edges.push(makeEdge(successNoop.id, out.to));
  }

  return { nodes: [...graph.nodes, successNoop], edges };
}

/**
 * Check whether failure-route nodes already have noop children on the success
 * path. If not, insert a Success noop and rewire (failure branch stays opt-in).
 *
 * Only reachable through `setGraph`, which nothing calls — a legacy node gets
 * its Success noop from `enableFailureRoute` instead.
 */
function ensureFailureRouteNoops(graph: OmegaGraph): OmegaGraph {
  return graph.nodes
    .filter((n) => nodeHasFailureRoute(n.type))
    .reduce((acc, node) => ensureSuccessNoop(acc, node.id), graph);
}

// =============================================================================
// setGraph
// =============================================================================

/**
 * Replace the entire graph. Performs basic validation:
 * - Must have exactly 1 start node
 * - Must have at least 1 end node
 * - All edge references must point to existing nodes
 *
 * Then post-processes the graph to:
 * - Normalize node parameters (fill schema defaults, handle params→parameters)
 * - Insert missing noop nodes for branching and failure-route nodes
 */
export function setGraph(newGraph: OmegaGraph): OmegaGraph {
  const startNodes = newGraph.nodes.filter((n) => n.type === "start");
  if (startNodes.length !== 1) {
    throw new Error(
      `Graph must have exactly 1 start node, found ${startNodes.length}`
    );
  }

  const endNodes = newGraph.nodes.filter((n) => n.type === "end");
  if (endNodes.length === 0) {
    throw new Error("Graph must have at least 1 end node");
  }

  const nodeIds = new Set(newGraph.nodes.map((n) => n.id));
  for (const edge of newGraph.edges) {
    if (!nodeIds.has(edge.from)) {
      throw new Error(
        `Edge ${edge.id} references non-existent source node: ${edge.from}`
      );
    }
    if (!nodeIds.has(edge.to)) {
      throw new Error(
        `Edge ${edge.id} references non-existent target node: ${edge.to}`
      );
    }
  }

  // 1. Normalize all nodes — fill in missing parameters from schema defaults
  const normalizedNodes = newGraph.nodes.map(normalizeNode);
  let graph: OmegaGraph = { nodes: normalizedNodes, edges: [...newGraph.edges] };

  // 2. Ensure branching nodes have noop intermediaries
  graph = ensureBranchingNoops(graph);

  // 3. Ensure failure-route nodes have Success/Failure noop children
  graph = ensureFailureRouteNoops(graph);

  return graph;
}

// =============================================================================
// SubGraph Operations
// =============================================================================

export function addSubGraph(
  graph: OmegaGraph,
  afterNodeId: string,
  name: string = "Sub Graph",
  description: string = "",
  id?: string
): OmegaGraph {
  return addSubGraphOperation(graph, afterNodeId, name, description, id);
}

/**
 * Add a node after a node that may be inside a SubGraph.
 * Recursively finds the containing graph, applies the operation, and
 * rebuilds the outer graph.
 */
export function addNodeAfterRecursive(
  graph: OmegaGraph,
  afterNodeId: string,
  nodeType: string,
  params?: Record<string, unknown>,
  name?: string,
  id?: string
): OmegaGraph {
  if (nodeIsContainer(nodeType)) {
    const found = findContainingGraph(graph, afterNodeId);
    if (!found) throw new Error(`Node not found: ${afterNodeId}`);
    const containerAdd = nodeType === "foreach" ? addForEachOperation : addSubGraphOperation;
    const modifiedInner = containerAdd(found.graph, afterNodeId, name, "", id);
    return replaceInnerGraph(graph, found.path, modifiedInner);
  }

  const found = findContainingGraph(graph, afterNodeId);
  if (!found) throw new Error(`Node not found: ${afterNodeId}`);

  if (found.path.length === 0) {
    return addNodeAfter(graph, afterNodeId, nodeType, params, name, id);
  }

  const modifiedInner = addNodeAfter(found.graph, afterNodeId, nodeType, params, name, id);
  return replaceInnerGraph(graph, found.path, modifiedInner);
}

/**
 * Insert a node between two nodes that may be inside a SubGraph.
 */
export function insertNodeBetweenRecursive(
  graph: OmegaGraph,
  sourceId: string,
  targetId: string,
  nodeType: string,
  params?: Record<string, unknown>,
  name?: string,
  id?: string
): OmegaGraph {
  const found = findContainingGraph(graph, sourceId);
  if (!found) throw new Error(`Node not found: ${sourceId}`);

  if (found.path.length === 0) {
    if (nodeIsContainer(nodeType)) {
      const containerInsert = nodeType === "foreach" ? insertForEachBetween : insertSubGraphBetween;
      return containerInsert(graph, sourceId, targetId, name ?? (nodeType === "foreach" ? "For Each" : "Sub Graph"), "", id);
    }
    return insertNodeBetween(graph, sourceId, targetId, nodeType, params, name, id);
  }

  const modifiedInner = insertNodeBetween(found.graph, sourceId, targetId, nodeType, params, name, id);
  return replaceInnerGraph(graph, found.path, modifiedInner);
}

/**
 * Delete a node that may be inside a SubGraph.
 * If deleting a SubGraph itself, removes it and all inner nodes.
 */
export function deleteNodeRecursive(
  graph: OmegaGraph,
  nodeId: string
): OmegaGraph {
  const topLevelNode = graph.nodes.find((n) => n.id === nodeId);
  if (topLevelNode?.type === "sub_graph" || topLevelNode?.type === "foreach") {
    return deleteNode(graph, nodeId);
  }

  const found = findContainingGraph(graph, nodeId);
  if (!found) throw new Error(`Node not found: ${nodeId}`);

  if (found.path.length === 0) {
    return deleteNode(graph, nodeId);
  }

  const modifiedInner = deleteNode(found.graph, nodeId);
  return replaceInnerGraph(graph, found.path, modifiedInner);
}

/**
 * Delete a success/failure branching node that may live inside a container's
 * inner graph. Disables the failure route (removing the failure noop and any
 * exclusive failure-branch content) and then removes the node — at the correct
 * nesting level.
 *
 * The plain top-level `disableFailureRoute`/`deleteNode` throw "Node not found"
 * for a node nested inside a foreach/sub_graph, so a failure-route node placed
 * inside a container could not be deleted without routing through its
 * containing graph.
 */
export function deleteSuccessFailureNodeRecursive(
  graph: OmegaGraph,
  nodeId: string
): OmegaGraph {
  const found = findContainingGraph(graph, nodeId);
  if (!found) throw new Error(`Node not found: ${nodeId}`);

  const withoutFailure = disableFailureRoute(found.graph, nodeId);
  const modifiedInner = deleteNode(withoutFailure, nodeId);

  if (found.path.length === 0) {
    return modifiedInner;
  }
  return replaceInnerGraph(graph, found.path, modifiedInner);
}

/**
 * Find a node anywhere in the graph (top level or nested inside any
 * container's inner graph) using a predicate. Returns the first match
 * found in a depth-first traversal, or `undefined`.
 */
export function findNodeRecursive(
  graph: OmegaGraph,
  predicate: (node: WorkflowNode) => boolean
): WorkflowNode | undefined {
  for (const node of graph.nodes) {
    if (predicate(node)) return node;
    if (isContainerOmegaNode(node)) {
      const inner = findNodeRecursive(node.graph, predicate);
      if (inner) return inner;
    }
  }
  return undefined;
}

/**
 * Update a node that may live inside a container's inner graph.
 * Mirrors `addNodeAfterRecursive` / `deleteNodeRecursive` — finds the
 * containing graph, applies `updateNode`, and rebuilds the outer graph.
 */
export function updateNodeRecursive(
  graph: OmegaGraph,
  nodeId: string,
  params: Record<string, unknown>
): OmegaGraph {
  const found = findContainingGraph(graph, nodeId);
  if (!found) {
    throw new Error(`Node not found: ${nodeId}`);
  }

  if (found.path.length === 0) {
    return updateNode(graph, nodeId, params);
  }

  const modifiedInner = updateNode(found.graph, nodeId, params);
  return replaceInnerGraph(graph, found.path, modifiedInner);
}

/**
 * Rename a node anywhere in the graph (including inside containers).
 * Used by the chat tools after `add_node`/`insert_node` to apply the
 * model-supplied display name to the newly created node.
 */
export function renameNodeRecursive(
  graph: OmegaGraph,
  nodeId: string,
  newName: string
): OmegaGraph {
  const found = findContainingGraph(graph, nodeId);
  if (!found) {
    throw new Error(`Node not found: ${nodeId}`);
  }

  const renamedInner: OmegaGraph = {
    nodes: found.graph.nodes.map((n) =>
      n.id === nodeId ? ({ ...n, name: newName } as WorkflowNode) : n
    ),
    edges: found.graph.edges,
  };

  if (found.path.length === 0) {
    return renamedInner;
  }
  return replaceInnerGraph(graph, found.path, renamedInner);
}

/**
 * Walk every node in the graph (top level and inside any container).
 * Used to diff before/after graphs across nesting boundaries (e.g. to
 * find a node added inside a sub_graph by `addNodeAfterRecursive`).
 */
export function flattenAllNodes(graph: OmegaGraph): WorkflowNode[] {
  const out: WorkflowNode[] = [];
  for (const node of graph.nodes) {
    out.push(node);
    if (isContainerOmegaNode(node)) {
      out.push(...flattenAllNodes(node.graph));
    }
  }
  return out;
}

// =============================================================================
// buildGraphFromSteps — convert AI step list to OmegaGraph
// =============================================================================
//
// KEY DESIGN: this function delegates to addNodeAfter() for every step,
// using the EXACT SAME code path as the builder UI. No reimplementation
// of branching, failure routes, or noop creation logic.
// =============================================================================

/**
 * Detect whether an AI-provided branch is really the "default" catch-all.
 * The AI sometimes models the default path as a named branch with
 * label "Default" / "default" and condition "true". We fold its sub-steps
 * into the auto-generated default noop instead of creating a duplicate.
 */
function isDefaultBranch(branch: WorkflowBranch): boolean {
  return (
    branch.label.toLowerCase() === "default" &&
    branch.condition.trim() === "true"
  );
}

// =============================================================================
// AI → Omega Parameter Normalization
// =============================================================================

function isOmegaParamShape(value: unknown): boolean {
  return (
    value != null &&
    typeof value === "object" &&
    "type" in (value as Record<string, unknown>) &&
    "value" in (value as Record<string, unknown>)
  );
}

function wrapStringAsOmegaParam(
  value: string
): { type: string; value: string } {
  if (value.includes("{{")) return { type: "template", value };
  if (value.startsWith("node.")) return { type: "cel", value };
  return { type: "literal", value };
}

/**
 * AI-friendly aliases for document_analysis:
 * - `files` string  -> `{ type: "cel", value }`
 * - legacy `fileCount` -> CEL expression referencing Start files
 * - `targetItems` -> `targetDocuments`
 */
function normalizeDocumentAnalysisParams(
  params: Record<string, unknown>,
  graph: OmegaGraph
): Record<string, unknown> {
  const result = { ...params };

  if (typeof result.files === "string") {
    result.files = { type: "cel" as const, value: result.files };
  }

  // Legacy compatibility with older prompts that used fileCount.
  if ("fileCount" in result && !("files" in result)) {
    const startNodeId = graph.nodes.find((n) => n.type === "start")?.id;
    result.files = {
      type: "cel" as const,
      value: `node.${startNodeId ?? START_NODE_ID}.files`,
    };
    delete result.fileCount;
  }

  if ("targetItems" in result && !("targetDocuments" in result)) {
    result.targetDocuments = result.targetItems;
    delete result.targetItems;
  }

  return result;
}

function inferSchemaProperty(val: unknown): Record<string, unknown> {
  if (Array.isArray(val)) {
    const first = val.length > 0 ? val[0] : undefined;
    if (first && typeof first === "object" && "type" in (first as Record<string, unknown>)) {
      return { type: "array", description: (first as Record<string, unknown>).description ?? "", items: first };
    }
    const desc = first != null ? String(first) : "";
    return { type: "array", description: desc, items: { type: "string", description: desc } };
  }
  if (typeof val === "number") return { type: "number", description: "" };
  if (typeof val === "boolean") return { type: "boolean", description: "" };
  if (val && typeof val === "object") return { type: "object", description: "" };
  return { type: "string", description: typeof val === "string" ? val : "" };
}

/**
 * Convert a simple { key: "description" } object from the AI into JSON Schema
 * that the agent's fromOmega transformer expects.
 */
function toJsonSchema(
  simple: Record<string, unknown>
): Record<string, unknown> {
  if ("type" in simple && simple.type === "object" && "properties" in simple) {
    return simple;
  }

  const properties: Record<string, Record<string, unknown>> = {};
  const required: string[] = [];

  for (const [key, val] of Object.entries(simple)) {
    properties[key] = inferSchemaProperty(val);
    required.push(key);
  }

  return { type: "object", properties, required };
}

/**
 * AI sends plain `desiredOutputFields` object and plain `agentInput` string.
 * Omega expects OmegaParameter-wrapped values with JSON Schema inside.
 */
function normalizeAgentParams(
  params: Record<string, unknown>
): Record<string, unknown> {
  const result = { ...params };

  if (result.desiredOutputFields) {
    if (isOmegaParamShape(result.desiredOutputFields)) {
      const wrapped = result.desiredOutputFields as { type: string; value: unknown };
      if (
        wrapped.type === "literal" &&
        wrapped.value &&
        typeof wrapped.value === "object"
      ) {
        result.desiredOutputFields = { ...wrapped, value: toJsonSchema(wrapped.value as Record<string, unknown>) };
      }
    } else {
      const schema = toJsonSchema(
        result.desiredOutputFields as Record<string, unknown>
      );
      result.desiredOutputFields = { type: "literal", value: schema };
    }
  }

  if (result.evalSampleRate == null) {
    result.evalSampleRate = { type: "literal", value: 0.1 };
  }

  if (typeof result.agentInput === "string") {
    result.agentInput = wrapStringAsOmegaParam(result.agentInput);
  }

  return result;
}

/**
 * Lowercase the `method` parameter on http_request nodes. The UI Select and
 * the fromOmega whitelist only accept lowercase ("get" | "post" | ...), but
 * the LLM frequently emits "GET", "Post", etc. Without normalization the
 * value is silently coerced to "get" on the next render, making the AI
 * appear to "fail" at setting the method.
 */
function normalizeHttpRequestParams(
  params: Record<string, unknown>
): Record<string, unknown> {
  const result = { ...params };
  const method = result.method;
  if (typeof method === "string") {
    result.method = method.toLowerCase();
  } else if (isOmegaParamShape(method)) {
    const wrapped = method as { type: string; value: unknown };
    if (typeof wrapped.value === "string") {
      result.method = { ...wrapped, value: wrapped.value.toLowerCase() };
    }
  }
  return result;
}

/**
 * The `pipe` (Data Transform) node stores its mappings inside a `data` record
 * (`{ variableName: OmegaParameter }`). autoWrapParams only wraps top-level
 * params, so the entries inside `data` never get type-detected and arrive as
 * raw strings — which fromOmega then flattens to static literals. Wrap each
 * entry here so `node.x.y` references become `cel`, `{{ }}` strings become
 * `template`, and everything else becomes `literal`.
 */
function wrapPipeDataEntry(value: unknown): unknown {
  if (typeof value === "string") {
    const cleaned = unescapeLiteralSequences(value);
    return { type: detectParamType(cleaned), value: cleaned };
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return { type: "literal", value };
  }
  if (isOmegaParamShape(value)) {
    // Already wrapped — still correct a mistyped string (e.g. literal -> cel).
    return fixOmegaParamType(value);
  }
  if (value != null && typeof value === "object") {
    // Object/array payloads map to a literal value.
    return { type: "literal", value };
  }
  return value;
}

function normalizePipeParams(
  params: Record<string, unknown>
): Record<string, unknown> {
  const data = params.data;
  if (data == null || typeof data !== "object" || Array.isArray(data)) {
    return params;
  }

  const wrappedData: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    wrappedData[key] = wrapPipeDataEntry(value);
  }

  return { ...params, data: wrappedData };
}

/**
 * Convert AI-friendly parameter shapes to the Omega API format that
 * fromOmega transformers expect. Called before autoWrapParams.
 */
export function normalizeAIParamsForType(
  nodeType: string,
  params: Record<string, unknown> | undefined,
  graph: OmegaGraph
): Record<string, unknown> | undefined {
  if (!params) return params;

  switch (nodeType) {
    case "document_analysis":
      return normalizeDocumentAnalysisParams(params, graph);
    case "agent":
      return normalizeAgentParams(params);
    case "http_request":
      return normalizeHttpRequestParams(params);
    case "pipe":
      return normalizePipeParams(params);
    default:
      return params;
  }
}

/**
 * Enable `acceptFiles` on the start node when any document_analysis node
 * exists in the graph. The document_analysis node references `node.{startId}.files`,
 * which only works when the start node accepts file uploads.
 */
export function ensureStartAcceptsFilesIfNeeded(graph: OmegaGraph): OmegaGraph {
  const hasDocAnalysis = graph.nodes.some((n) => n.type === "document_analysis");
  if (!hasDocAnalysis) return graph;

  const startNode = graph.nodes.find((n) => n.type === "start");
  if (!startNode) return graph;

  const startWithMeta = startNode as WorkflowNode & { metadata?: Record<string, unknown> };
  if (startWithMeta.metadata?.acceptFiles) return graph;

  return {
    ...graph,
    nodes: graph.nodes.map((n) =>
      n.type === "start"
        ? { ...n, metadata: { ...(n as WorkflowNode & { metadata?: Record<string, unknown> }).metadata, acceptFiles: true } }
        : n
    ),
  };
}

/**
 * Unescape literal backslash sequences that LLMs sometimes double-escape.
 * After JSON parsing, real newlines are already `\n` chars; leftover literal
 * two-char sequences like `\` + `n` mean the model double-escaped them.
 */
function unescapeLiteralSequences(value: string): string {
  return value.replaceAll(String.raw`\n`, "\n").replaceAll(String.raw`\t`, "\t");
}

/**
 * Matches a CEL-style node reference anywhere in the string, e.g.
 *   `node.abc.data`, `size(node.items.data) > 0`, `5 + node.x.y`.
 * Requires a word-boundary before `node.` so we don't match incidental
 * substrings like `surnode.foo`. The character after `.` must be the start
 * of a JS-style identifier.
 */
const CEL_REF_REGEX = /\bnode\.[A-Za-z_]/;
const URL_SCHEME_REGEX = /^[a-z][a-z\d+.-]*:\/\//i;

/**
 * Detect the correct OmegaParameter type from a string value.
 *
 * Rules (in order):
 * 1. If the string contains `{{`, it's an explicit template (string with
 *    embedded values for human-readable interpolation).
 * 2. If the string contains a `node.<ident>` reference anywhere — pure path
 *    or a CEL expression with operators/function calls — it's CEL. Cel
 *    preserves the source type at runtime; template would stringify it.
 * 3. Otherwise it's a literal constant.
 */
function detectParamType(value: string): "template" | "cel" | "literal" {
  const trimmed = value.trim();
  if (trimmed.includes("{{")) return "template";
  if (!URL_SCHEME_REGEX.test(trimmed) && CEL_REF_REGEX.test(trimmed)) {
    return "cel";
  }
  return "literal";
}

/**
 * If value is already an OmegaParam-shaped object with a string value,
 * ensure the `type` matches the actual content (template/cel/literal).
 */
function fixOmegaParamType(value: unknown): unknown {
  if (
    value != null &&
    typeof value === "object" &&
    "type" in (value as Record<string, unknown>) &&
    "value" in (value as Record<string, unknown>)
  ) {
    const wrapped = value as { type: string; value: unknown };
    if (typeof wrapped.value === "string") {
      const cleaned = unescapeLiteralSequences(wrapped.value);
      const correctType = detectParamType(cleaned);
      if (wrapped.type !== correctType || cleaned !== wrapped.value) {
        return { ...wrapped, type: correctType, value: cleaned };
      }
    }
  }
  return value;
}

/**
 * Auto-wrap primitive param values into OmegaParameter format.
 *
 * Allows the AI to write simplified params:
 *   `{ "to": "user@example.com" }` → `{ "to": { type: "literal", value: "user@example.com" } }`
 *   `{ "to": "node.rand.value" }` → `{ "to": { type: "cel", value: "node.rand.value" } }`
 *   `{ "body": "Hi {{ node.rand.value }}" }` → `{ "body": { type: "template", value: "..." } }`
 *
 * Already-formatted OmegaParameter objects pass through unchanged.
 * Non-parameter fields (like `contentType`, `branches`) pass through unchanged.
 */
export function autoWrapParams(
  nodeType: string,
  params?: Record<string, unknown>
): Record<string, unknown> | undefined {
  if (!params) return params;

  const defaults = getDefaultParametersForType(nodeType);
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(params)) {
    const defaultVal = defaults?.[key];
    const fieldIsOmegaParam =
      defaultVal != null &&
      typeof defaultVal === "object" &&
      "type" in (defaultVal as Record<string, unknown>) &&
      "value" in (defaultVal as Record<string, unknown>);

    if (
      fieldIsOmegaParam &&
      (typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean")
    ) {
      if (typeof value === "string") {
        const cleaned = unescapeLiteralSequences(value);
        result[key] = { type: detectParamType(cleaned), value: cleaned };
      } else {
        result[key] = { type: "literal", value };
      }
    } else {
      result[key] = fixOmegaParamType(value);
    }
  }

  return result;
}

/**
 * Find a newly-created node by comparing before/after graph snapshots.
 * Returns the first new node matching the given type.
 */
function findNewNode(
  prevNodeIds: Set<string>,
  graph: OmegaGraph,
  nodeType: string
): WorkflowNode {
  const found = graph.nodes.find(
    (n) => !prevNodeIds.has(n.id) && n.type === nodeType
  );
  if (!found) {
    throw new Error(`Could not find newly added ${nodeType} node`);
  }
  return found;
}

function nodesReachableFrom(graph: OmegaGraph, startId: string): Set<string> {
  const visited = new Set<string>();
  const queue: string[] = [startId];
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (visited.has(id)) continue;
    visited.add(id);
    for (const e of graph.edges) {
      if (e.from === id) queue.push(e.to);
    }
  }
  return visited;
}

function getSuccessNoopIdForFailureRoute(
  graph: OmegaGraph,
  nodeId: string,
  failureEdgeId?: string
): string | undefined {
  const out = outgoingEdges(graph, nodeId);
  for (const e of out) {
    if (failureEdgeId && e.id === failureEdgeId) continue;
    const target = graph.nodes.find((n) => n.id === e.to);
    if (target?.type === "noop" && target.name === "Success") {
      return target.id;
    }
  }
  const failTo =
    failureEdgeId && graph.edges.find((x) => x.id === failureEdgeId)?.to;
  for (const e of out) {
    if (failureEdgeId && e.id === failureEdgeId) continue;
    const target = graph.nodes.find((n) => n.id === e.to);
    if (target?.type === "noop" && e.to !== failTo) {
      return target.id;
    }
  }
  return undefined;
}

/** Find the success noop ID for a failure-route node. */
function findSuccessNoopId(
  graph: OmegaGraph,
  nodeId: string
): string | undefined {
  const node = graph.nodes.find((n) => n.id === nodeId);
  if (!node) return undefined;
  const failEdgeId = (node as unknown as Record<string, unknown>)
    .onFailureEdgeId as string | undefined;
  return getSuccessNoopIdForFailureRoute(graph, nodeId, failEdgeId);
}

/**
 * Computes the set of node IDs exclusively on the failure path: reachable from
 * failNoop but not accessible from outside the failure subgraph.
 *
 * Uses a fixpoint refinement after the initial F−S diff: any candidate that has
 * an inbound edge from outside the exclusive set is removed (and the process
 * repeats until stable). The failure edge itself (failEdgeId) is ignored because
 * it is the edge being removed.
 *
 * This prevents incorrectly marking nodes as exclusive when they are reachable
 * from other parts of the graph via additional incoming edges.
 */
function computeExclusiveFailureNodes(
  graph: OmegaGraph,
  failEdgeId: string,
  failNoopId: string,
  successNoopId: string | undefined
): Set<string> {
  const S = successNoopId
    ? nodesReachableFrom(graph, successNoopId)
    : new Set<string>();
  const F = nodesReachableFrom(graph, failNoopId);

  let exclusive = new Set([...F].filter((id) => !S.has(id)));

  let changed = true;
  while (changed) {
    changed = false;
    for (const id of [...exclusive]) {
      const hasExternalInbound = graph.edges.some(
        (e) => e.to === id && e.id !== failEdgeId && !exclusive.has(e.from)
      );
      if (hasExternalInbound) {
        exclusive.delete(id);
        changed = true;
      }
    }
  }

  return exclusive;
}

/**
 * Whether disabling failure handling would remove nodes not reachable from the success path.
 */
export function failureBranchHasExclusiveContent(
  graph: OmegaGraph,
  nodeId: string
): boolean {
  const node = graph.nodes.find((n) => n.id === nodeId);
  if (!node || !nodeHasFailureRoute(node.type)) return false;
  const onFailureEdgeId = (node as WorkflowNode & { onFailureEdgeId?: string })
    .onFailureEdgeId;
  if (!onFailureEdgeId) return false;

  const failEdge = graph.edges.find((e) => e.id === onFailureEdgeId);
  if (!failEdge) return false;
  const failNoopId = failEdge.to;

  const successNoopId = getSuccessNoopIdForFailureRoute(
    graph,
    nodeId,
    onFailureEdgeId
  );

  const exclusive = computeExclusiveFailureNodes(
    graph,
    onFailureEdgeId,
    failNoopId,
    successNoopId
  );

  // Show confirmation only when there is content beyond the bare failNoop that
  // will be deleted. Nodes that converge back into the graph from outside
  // (detected by the fixpoint above) are not considered exclusive.
  return [...exclusive].some((id) => id !== failNoopId);
}

export function collectExclusiveFailureNodeIds(
  graph: OmegaGraph,
  nodeId: string
): Set<string> {
  const node = graph.nodes.find((n) => n.id === nodeId);
  if (!node || !nodeHasFailureRoute(node.type)) return new Set();
  const onFailureEdgeId = (node as WorkflowNode & { onFailureEdgeId?: string })
    .onFailureEdgeId;
  if (!onFailureEdgeId) return new Set();
  const failEdge = graph.edges.find((e) => e.id === onFailureEdgeId);
  if (!failEdge) return new Set();
  const failNoopId = failEdge.to;
  const successNoopId = getSuccessNoopIdForFailureRoute(
    graph,
    nodeId,
    onFailureEdgeId
  );
  return computeExclusiveFailureNodes(
    graph,
    onFailureEdgeId,
    failNoopId,
    successNoopId
  );
}

export function enableFailureRoute(graph: OmegaGraph, nodeId: string): OmegaGraph {
  const node = graph.nodes.find((n) => n.id === nodeId);
  if (!node || !nodeHasFailureRoute(node.type)) {
    throw new Error(`Cannot enable failure route for node: ${nodeId}`);
  }
  const existing = (node as WorkflowNode & { onFailureEdgeId?: string })
    .onFailureEdgeId;
  if (existing) return graph;

  // Nodes saved before this type had a failure route go straight to their
  // successor; give them the Success noop the failure branch pairs with.
  const withSuccess = ensureSuccessNoop(graph, nodeId);

  const failNoop = makeNoop("Failure");
  const failEdge = makeEdge(nodeId, failNoop.id);

  const nodes = [
    ...withSuccess.nodes.map((n) =>
      n.id === nodeId
        ? ({ ...n, onFailureEdgeId: failEdge.id } as WorkflowNode)
        : n
    ),
    failNoop,
  ];
  const edges = [...withSuccess.edges, failEdge];

  return { nodes, edges };
}

export function disableFailureRoute(graph: OmegaGraph, nodeId: string): OmegaGraph {
  const node = graph.nodes.find((n) => n.id === nodeId);
  if (!node || !nodeHasFailureRoute(node.type)) {
    throw new Error(`Cannot disable failure route for node: ${nodeId}`);
  }
  const onFailureEdgeId = (node as WorkflowNode & { onFailureEdgeId?: string })
    .onFailureEdgeId;
  if (!onFailureEdgeId) return graph;

  const failEdge = graph.edges.find((e) => e.id === onFailureEdgeId);
  if (!failEdge) {
    return {
      nodes: graph.nodes.map((n) =>
        n.id === nodeId
          ? ({ ...n, onFailureEdgeId: undefined } as WorkflowNode)
          : n
      ),
      edges: graph.edges,
    };
  }

  const toRemove = collectExclusiveFailureNodeIds(graph, nodeId);

  let nodes = graph.nodes.filter((n) => !toRemove.has(n.id));
  let edges = graph.edges.filter(
    (e) => !toRemove.has(e.from) && !toRemove.has(e.to)
  );

  nodes = nodes.map((n) =>
    n.id === nodeId
      ? ({ ...n, onFailureEdgeId: undefined } as WorkflowNode)
      : n
  );

  return { nodes, edges };
}

/** Find branch noop IDs for a split node (in order matching branches array). */
function findBranchNoopIds(
  graph: OmegaGraph,
  splitNodeId: string
): string[] {
  const node = graph.nodes.find((n) => n.id === splitNodeId);
  if (!node) return [];
  const params = getNodeParams(node);
  const branches = (params?.branches as BranchSpec[]) ?? [];
  return branches
    .map((b) => {
      const edge = graph.edges.find((e) => e.id === b.edgeId);
      return edge?.to;
    })
    .filter((id): id is string => id != null);
}

/** Find the default noop ID for a split node. */
function findDefaultNoopId(
  graph: OmegaGraph,
  splitNodeId: string
): string | undefined {
  const node = graph.nodes.find((n) => n.id === splitNodeId);
  if (!node) return undefined;
  const params = getNodeParams(node);
  const defaultEdgeId = params?.defaultEdgeId as string | undefined;
  if (!defaultEdgeId) return undefined;
  const edge = graph.edges.find((e) => e.id === defaultEdgeId);
  return edge?.to;
}

/**
 * Convert AI step branches to Omega branch params for addNodeAfter.
 * Filters out any "Default" branches (those are handled by the auto-generated default).
 */
function toOmegaBranchParams(
  step: WorkflowStep
): Record<string, unknown> | undefined {
  if (!step.branches) return step.params;

  const namedBranches = step.branches.filter((b) => !isDefaultBranch(b));
  return {
    ...step.params,
    branches: namedBranches.map((b) => ({
      label: b.label,
      check: { type: "cel", value: b.condition },
    })),
  };
}

/**
 * Process a linear chain of steps by calling addNodeAfter for each one.
 *
 * Uses the SAME code path as the builder UI — no reimplementation of
 * branching, failure routes, or noop logic.
 *
 * Returns the modified graph and the IDs of all "tail" nodes (the final
 * nodes in each path that need to be connected to whatever comes next).
 */
function buildChain(
  steps: WorkflowStep[],
  afterId: string,
  graph: OmegaGraph
): { graph: OmegaGraph; tailIds: string[] } {
  let currentGraph = graph;
  let currentTailIds: string[] = [afterId];

  for (const step of steps) {
    // For multiple tails (after a split), add the node after the first tail
    // and manually connect remaining tails → fan-in convergence.
    const primaryAfterId = currentTailIds[0];
    const prevNodeIds = new Set(currentGraph.nodes.map((n) => n.id));

    // Prepare params: normalize AI-friendly names → Omega, then auto-wrap
    const rawParams = nodeIsBranching(step.type)
      ? toOmegaBranchParams(step)
      : step.params;
    const normalizedParams = normalizeAIParamsForType(
      step.type,
      rawParams,
      currentGraph
    );
    const wrappedParams = autoWrapParams(step.type, normalizedParams);

    currentGraph = addNodeAfter(
      currentGraph,
      primaryAfterId,
      step.type,
      wrappedParams,
      step.name,
      step.ref
    );

    const newNode = findNewNode(prevNodeIds, currentGraph, step.type);

    // Fan-in: connect any remaining previous tails to the new node
    for (let i = 1; i < currentTailIds.length; i++) {
      currentGraph = {
        ...currentGraph,
        edges: [
          ...currentGraph.edges,
          makeEdge(currentTailIds[i], newNode.id),
        ],
      };
    }

    // Determine new tails based on node type
    if (nodeIsBranching(step.type) && step.branches) {
      const branchTails = buildBranchSteps(step, newNode.id, currentGraph);
      currentGraph = branchTails.graph;
      currentTailIds = branchTails.tailIds;
    } else if (nodeHasFailureRoute(step.type)) {
      const successId = findSuccessNoopId(currentGraph, newNode.id);
      currentTailIds = successId ? [successId] : [newNode.id];
    } else {
      currentTailIds = [newNode.id];
    }
  }

  return { graph: currentGraph, tailIds: currentTailIds };
}

/**
 * Process branch sub-steps for a split node.
 * Recursively calls buildChain for each branch that has steps.
 * Empty branches and the default noop dangle as placeholders.
 */
function buildBranchSteps(
  step: WorkflowStep,
  splitNodeId: string,
  graph: OmegaGraph
): { graph: OmegaGraph; tailIds: string[] } {
  let currentGraph = graph;
  const tailIds: string[] = [];

  const namedBranches = (step.branches ?? []).filter(
    (b) => !isDefaultBranch(b)
  );
  const aiDefaultBranch = (step.branches ?? []).find((b) =>
    isDefaultBranch(b)
  );

  // Process named branch sub-steps
  const branchNoopIds = findBranchNoopIds(currentGraph, splitNodeId);
  for (let i = 0; i < namedBranches.length && i < branchNoopIds.length; i++) {
    const branch = namedBranches[i];
    if (branch.steps.length > 0) {
      const result = buildChain(branch.steps, branchNoopIds[i], currentGraph);
      currentGraph = result.graph;
      tailIds.push(...result.tailIds);
    }
    // Empty branches: noop dangles — NOT added to tailIds
  }

  // Process AI's "Default" branch sub-steps (folded into auto-generated default)
  if (aiDefaultBranch && aiDefaultBranch.steps.length > 0) {
    const defaultNoopId = findDefaultNoopId(currentGraph, splitNodeId);
    if (defaultNoopId) {
      const result = buildChain(
        aiDefaultBranch.steps,
        defaultNoopId,
        currentGraph
      );
      currentGraph = result.graph;
      tailIds.push(...result.tailIds);
    }
  }

  return { graph: currentGraph, tailIds };
}

/**
 * Ensure all node names are unique — Omega validation requires this.
 * Appends " 2", " 3", etc. to duplicate names.
 */
export function deduplicateNodeNames(graph: OmegaGraph): OmegaGraph {
  const nameCount = new Map<string, number>();
  const nodes = graph.nodes.map((node) => {
    const count = (nameCount.get(node.name) ?? 0) + 1;
    nameCount.set(node.name, count);
    if (count > 1) {
      return { ...node, name: `${node.name} ${count}` } as WorkflowNode;
    }
    return node;
  });
  return { nodes, edges: graph.edges };
}

/**
 * Cap each tail with its own End. A shared top-level End is illegal in the
 * builder (no second inbound on a Terminator); only `sub_graph_end` may have
 * fan-in. Duplicate "End" names are resolved by `deduplicateNodeNames`.
 */
function capTailsWithDedicatedEnds(
  graph: OmegaGraph,
  tailIds: string[]
): OmegaGraph {
  if (tailIds.length === 0) return graph;

  const endNodes = tailIds.map(() => makeNode("end", "End"));
  return {
    nodes: [...graph.nodes, ...endNodes],
    edges: [
      ...graph.edges,
      ...tailIds.map((tailId, i) => makeEdge(tailId, endNodes[i].id)),
    ],
  };
}

/**
 * Build a complete OmegaGraph from a flat/nested list of workflow steps.
 *
 * Delegates to `addNodeAfter()` for each step — the same path as the builder
 * UI — so branching, failure routes, and noops stay consistent. Start is
 * minted with `START_NODE_ID`. Empty branch noops dangle as placeholders.
 *
 * Each tail that has steps gets its own End. Top-level graphs cannot fan in
 * to a shared terminal; that is only legal on a container's `sub_graph_end`.
 */
export function buildGraphFromSteps(steps: WorkflowStep[]): OmegaGraph {
  // 1. Start with just a start node (no end — avoids unwanted edge splitting).
  //    Uses the canonical START_NODE_ID so `node.start.payload.*` refs resolve.
  const startNode = makeNode("start", "Start", undefined, START_NODE_ID);
  let graph: OmegaGraph = { nodes: [startNode], edges: [] };

  // 2. Add each step via addNodeAfter — same code path as the builder
  const result = buildChain(steps, startNode.id, graph);
  graph = capTailsWithDedicatedEnds(result.graph, result.tailIds);

  // 3. Ensure all node names are unique (Omega validation requires this).
  //    Handles duplicate noop names like "Default", "Success", etc.
  return deduplicateNodeNames(graph);
}
